import React, { useEffect, useState } from "react";
import { CellProps } from "react-table";
import { AnimatePresence, motion } from "framer-motion";
import getFontAwesomeIcon from "../../../../../utils/getFontAwesomeIcon";
import StatusColorDot from "../../sharedTableComponents/StatusColorDot";
import { BaseToolTip, CopyToClipboardIconWrapper } from "../../..";
import {
	baseFormatTableData,
	generateAccessors,
} from "@agilant/toga-blox/dist/utils/formaTableData";
import CopyableCell from "../components/tableActions/CopyableCell";
import { useTableStore } from "../../../../../stores";
import {
	CustomTableColumn,
	FormatTableDataParams,
} from "../../../../../utils/formatTableDataTypes";
import { TableTooltipCell } from "..";

export function formatTableData({
	columnTitles,
	accessors,
	columnConfigs,
	typeValues,
	showFilterSlugToggle,
	visibleListId,
	handleIconClick,
	actionList,
	tableData,
	deactivatedMenuUuids,
	showStickyColumn,
	leftLockedColumnCount,
	suffixColumnIndexes = [],
	cellClickAction = () => {},
	isNestedTable,
	activeRowUuid = "",
	hasCustomCellToggle = false,
	tableDisplayData,
}: FormatTableDataParams): CustomTableColumn[] {
	const baseColumns = baseFormatTableData(
		columnTitles,
		accessors,
		columnConfigs || []
	);

	const effectiveSuffixes =
		suffixColumnIndexes.length > 0
			? suffixColumnIndexes
			: [leftLockedColumnCount];

	const columns = baseColumns
		.map((col, i) => {
			const key = `col${i + 1}`;

			const rawConfig = {
				key,
				type: "DEFAULT",
				header: columnTitles[i] ?? "",
				isFilterable: false,
				isSortable: false,
				isCopyable: false,
				hasSuffix: true,
				precision: 0,
				...columnConfigs.find((c: any) => c.key === key),
			};

			if (rawConfig.type === "STATUS") {
				const originalCell = col.Cell;
				return {
					...col,
					headerText: columnTitles[i] || "",
					Cell: (cellProps: CellProps<any>) => (
						<div className={` flex items-center`}>
							<div className="flex mr-2 size-1 mb-1">
								<StatusColorDot
									status={cellProps.value as string}
									typeValues={typeValues}
								/>
							</div>

							<div>{originalCell(cellProps)}</div>
						</div>
					),
				};
			}
			if (rawConfig.type === "BOOLEAN") {
				const originalCell = col.Cell;
				return {
					...col,
					headerText: columnTitles[i] || "",
					Cell: (cellProps: CellProps<any>) => (
						<div className={` flex items-center`}>
							<StatusColorDot
								status={cellProps.value as string}
								typeValues={typeValues || {}}
								additionalClasses="flex items-center mr-1"
								useCustomColor={
									cellProps.value === "True"
										? "#2CB224"
										: cellProps.value === "False"
										? "#FF2E54"
										: undefined
								}
							/>

							<div>{originalCell(cellProps)}</div>
						</div>
					),
				};
			}

			if (rawConfig.type === "CURRENCY" || rawConfig.type === "NUMBER") {
				const originalCell = col.Cell!;
				const decimals = rawConfig.precision;

				return {
					...col,
					headerText: columnTitles[i] || "",
					Cell: (cellProps: CellProps<any>) => {
						const val = cellProps.value as number;

						// Currency: always show exactly `decimals` places, plus icon
						if (rawConfig.type === "CURRENCY") {
							const formatted = val.toLocaleString("en-US", {
								minimumFractionDigits: decimals,
								maximumFractionDigits: decimals,
							});
							return (
								<div className="flex w-full items-center">
									<div className="flex-1">
										{getFontAwesomeIcon(
											"dollarSign",
											"regular"
										)}
									</div>
									<div>{formatted}</div>
								</div>
							);
						}

						// Number: show up to `decimals` places, but drop trailing zeros if unneeded
						const formattedNumber = val.toLocaleString("en-US", {
							minimumFractionDigits: 0,
							maximumFractionDigits: decimals,
						});
						return (
							<div className="flex text-left p-1 items-center">
								{formattedNumber}
							</div>
						);
					},
				};
			}

			if (rawConfig.hasSuffix && effectiveSuffixes.includes(i)) {
				return {
					...col,
					headerText: columnTitles[i] || "",
					Cell: (cellProps: CellProps<any>) => {
						const rowIdx = cellProps.row.index;
						const rowUuid = tableData?.[rowIdx]?.data?.uuid!;
						const isNonSerialized =
							tableData?.[rowIdx]?.data?.inventorytype ===
							"NON_SERIALIZED";
						const handleClick = (
							e: React.MouseEvent<HTMLDivElement>
						) => cellClickAction(e, rowUuid);

						if (isNonSerialized) {
							const originalCell = col.Cell!;
							if (rawConfig.isCopyable) {
								return (
									<div className="flex">
										<CopyableCell
											originalCell={originalCell}
											{...cellProps}
										/>
									</div>
								);
							}

							// otherwise just render the tooltip:
							return (
								<div className="text-left">
									{cellProps.value}
								</div>
							);
						} else {
							// the core tooltip cell:
							const tooltip = (
								<TableTooltipCell
									{...cellProps}
									tableData={[]}
									cellClickAction={(e) => {
										handleClick?.(
											e as React.MouseEvent<
												HTMLDivElement,
												MouseEvent
											>
										);
									}}
									icon="chevronsRight"
									position="right"
									tipText="See units"
								/>
							);

							// if this column is copyable, wrap it:
							if (rawConfig.isCopyable) {
								return (
									<div className="flex">
										<CopyableCell
											originalCell={() => tooltip}
											{...cellProps}
										/>
									</div>
								);
							}

							// otherwise just render the tooltip:
							return <div className="text-left">{tooltip}</div>;
						}
					},
				} as CustomTableColumn;
			}

			if (rawConfig.isCopyable) {
				const originalCell = col.Cell;
				return {
					...col,
					headerText: columnTitles[i] || "",
					Cell: (cellProps: CellProps<any>) => (
						<div className="flex">
							<CopyableCell
								originalCell={originalCell}
								{...cellProps}
							/>
						</div>
					),
				};
			}

			// If rawConfig.isVisible set to false skip the column
			if (rawConfig.isVisible === false) {
				return null;
			}

			return {
				...col,
				headerText: columnTitles[i] || "",
			};
		})
		.filter((c): c is CustomTableColumn => c !== null);

	const ACTION_COL_W = showFilterSlugToggle ? 98 : 64;

	{
		showStickyColumn &&
			columns.push({
				headerText: "",
				Header: () => {
					return <></>;
				},

				accessor: "sticky-column",
				sticky: "right",
				width: ACTION_COL_W,
				minWidth: ACTION_COL_W,
				maxWidth: ACTION_COL_W,
				maxHeight: 1,
				className: `relative`,

				Cell: ({ row, data, index }: any) => {
					const id = row?.id;
					const isFirstRow = row.index === 0;
					let rowUuid = "";
					if (id && tableData && tableData[id]?.data) {
						rowUuid = tableData[id].data.uuid;
					}

					const totalRows = data?.length || 0;
					const isLastTwoRows = row?.index >= totalRows - 2;
					const toolTipPosition = deactivatedMenuUuids?.includes(
						rowUuid
					)
						? "left"
						: "top";

					const actionListClass = ` ${
						isLastTwoRows && totalRows >= 4
							? "top-[-30px]"
							: "top-[-10px]"
					} absolute right-40 size-12`;

					const wrapperClass = [
						showFilterSlugToggle ? "min-w-24" : "min-w-16",
						"  w-full  bg-gray-50 flex justify-center items-center ",
						!isFirstRow && "sticky-column-border ",
						isNestedTable && "hidden",
					]
						.filter(Boolean)
						.join(" ");

					const motionDivClass = `${
						visibleListId === id
							? "border-primary bg-supply-blue-50 "
							: "bg-white"
					} ${
						deactivatedMenuUuids?.includes(rowUuid)
							? "text-navy-200 cursor-not-allowed hover:border-navy-200"
							: "bg-white text-primary hover:border-primary hover:bg-supply-blue-50"
					} mr-3 relative rounded border-1 border-navy-200 text-base p-1 size-6 flex items-center justify-center`;

					const arrowDivClass =
						"relative bg-white hover:bg-supply-blue-50 hover:border-primary text-primary rounded border-1 border-navy-200 text-base p-1 size-6 flex items-center justify-center";
					const stampDivClass = `p-2 rounded relative`;

					// ----- LOCAL STATE FOR SHOW/HIDE AND POSITION -----

					const [isMenuOpen, setIsMenuOpen] = useState(false);
					const [menuPosition, setMenuPosition] = useState<{
						top: number;
						left: number;
					} | null>(null);

					// ----- GLOBAL STORE FOR A SINGLE OPEN ROW -----
					const openRowUuid = useTableStore(
						(state) => state.openRowUuid
					);
					const setOpenRowUuid = useTableStore(
						(state) => state.setOpenRowUuid
					);
					const setSelectedRowId = useTableStore(
						(state) => state.setSelectedRowId
					);
					const setActiveRowUuid = useTableStore(
						(state) => state.setActiveRowUuid
					);
					const isActive = rowUuid === activeRowUuid;

					useEffect(() => {
						if (!rowUuid) return;

						if (openRowUuid === rowUuid) {
							setActiveRowUuid(rowUuid);
							setSelectedRowId(rowUuid);
							setIsMenuOpen(true);
						} else {
							setIsMenuOpen(false);
						}
					}, [openRowUuid, rowUuid]);

					return (
						<div
							style={{
								left: showFilterSlugToggle ? "-10px" : "",
							}}
							className={wrapperClass}
						>
							<div
								className={`${
									isFirstRow
										? "absolute top-[-40px] bg-supply-blue-50 h-8 z-[9999]"
										: "hidden"
								}`}
								style={{
									width: ACTION_COL_W,
								}}
							></div>
							{showFilterSlugToggle && (
								<div
									onClick={(e) => {
										e.stopPropagation();
										if (
											!deactivatedMenuUuids?.includes(
												rowUuid
											)
										) {
											handleIconClick &&
												handleIconClick(id, rowUuid);
										}
									}}
									key={id}
									className={motionDivClass}
								>
									<div className={stampDivClass}>
										<BaseToolTip
											message={"Approval"}
											toolTipPlacement={toolTipPosition}
										>
											{getFontAwesomeIcon(
												"stamp",
												"regular"
											)}
										</BaseToolTip>
									</div>

									<AnimatePresence>
										{visibleListId === id && (
											<motion.div
												key={`actionList-${id}`}
												initial="hidden"
												animate="visible"
												exit="exit"
												// variants={actionMenuVariant}
												className={actionListClass}
											>
												{actionList}
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							)}

							<div
								className={`${
									isNestedTable ? "" : arrowDivClass
								}`}
							>
								{isNestedTable ? (
									<></>
								) : (
									<BaseToolTip
										message={"Open"}
										toolTipPlacement={toolTipPosition}
									>
										{getFontAwesomeIcon(
											"arrowUpRight",
											"regular"
										)}
									</BaseToolTip>
								)}
							</div>
						</div>
					);
				},
			} as CustomTableColumn);
	}
	if (leftLockedColumnCount && leftLockedColumnCount > 0) {
		let leftOffset = -0;
		for (
			let idx = 0;
			idx < leftLockedColumnCount && idx < columns.length;
			idx++
		) {
			const col = columns[idx];
			const firstCol = col.accessor === "col1";
			const baseWidth =
				typeof (col as any).width === "number"
					? (col as any).width
					: typeof (col as any).minWidth === "number"
					? (col as any).minWidth
					: 165;
			const widthPx = baseWidth;

			const existingClass = (col as any).className ?? "";
			const zIndexClass = "z-10";
			const bgClass = "w-full text-sm flex relative w-full";
			const OriginalCell = (col as any).Cell;
			const rawTitle = columnTitles[idx];
			columns[idx] = {
				...(col as any),
				headerText: rawTitle,
				accessor: (col as any).accessor,
				sticky: "left",
				className: [existingClass, zIndexClass]
					.filter(Boolean)
					.join(" "),
				style: {
					...((col as any).style || {}),
					position: "sticky",
					left: leftOffset,
					top: 0,
				},
				Cell: (cellProps: CellProps<any>) => {
					return (
						<div
							className={`${zIndexClass} ${bgClass}
						  min-w-[200px]
						`}
						>
							<span className="flex text-left items-center flex-1 h-full">
								{OriginalCell
									? OriginalCell(cellProps)
									: cellProps.value}
							</span>
						</div>
					);
				},

				Header: () => (
					<div className={`z-50 text-sm text-left `}>{rawTitle}</div>
				),
			};
 			if (tableDisplayData && tableDisplayData.leftOffset) {
				leftOffset += widthPx + tableDisplayData.leftOffset;
			}
		}
	}
	return columns;
}

export function generateSupplyAccessors(columnTitles: string[]): string[] {
	return generateAccessors(columnTitles);
}
